import { useEffect, useState } from "react";
import { Monitor, Moon, Sun } from "lucide-react";

import {
  applyTheme,
  readStoredTheme,
  storeTheme,
  type ThemeChoice,
} from "@/hooks/useTheme";

const OPTIONS: { key: ThemeChoice; icon: typeof Sun; label: string }[] = [
  { key: "dark", icon: Moon, label: "Oscuro" },
  { key: "light", icon: Sun, label: "Claro" },
  { key: "auto", icon: Monitor, label: "Sistema" },
];

interface Props {
  /** Render as inline pill (default) or full-width row. */
  variant?: "pill" | "row";
}

/**
 * Three-way theme picker with the same UX as Argos Edge:
 *
 *   * ``dark``  — pinned dark mode.
 *   * ``light`` — pinned light mode.
 *   * ``auto``  — follows ``prefers-color-scheme`` and reacts to
 *                  OS-level changes while mounted.
 *
 * The choice persists in ``localStorage`` under ``argos.theme`` and
 * is applied at app boot by :mod:`main.tsx` so the first paint never
 * flashes the wrong palette.
 */
export function ThemeToggle({ variant = "pill" }: Props) {
  const [choice, setChoice] = useState<ThemeChoice>(() => readStoredTheme());

  useEffect(() => {
    applyTheme(choice);
    storeTheme(choice);
  }, [choice]);

  // While ``auto`` is active, react to OS-level theme changes
  // immediately — without this the user has to reload to see the
  // palette flip.
  useEffect(() => {
    if (choice !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("auto");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [choice]);

  if (variant === "row") {
    return (
      <div
        className="flex items-center justify-between gap-2 px-3 py-2"
        data-testid="theme-toggle"
      >
        <span className="text-xs text-muted-200">Tema</span>
        <ButtonGroup choice={choice} onChange={setChoice} />
      </div>
    );
  }

  return (
    <div className="inline-flex" data-testid="theme-toggle">
      <ButtonGroup choice={choice} onChange={setChoice} />
    </div>
  );
}

function ButtonGroup({
  choice,
  onChange,
}: {
  choice: ThemeChoice;
  onChange: (next: ThemeChoice) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden">
      {OPTIONS.map(({ key, icon: Icon, label }) => {
        const active = choice === key;
        return (
          <button
            key={key}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={active}
            onClick={() => onChange(key)}
            data-testid={`theme-toggle-${key}`}
            className={
              "px-2 py-1 text-xs transition " +
              (active
                ? "bg-primary text-white"
                : "bg-surface2 text-muted-200 hover:text-foreground")
            }
          >
            <Icon size={14} />
          </button>
        );
      })}
    </div>
  );
}
